import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslations } from '@ifixit/i18n/hooks';
import { fireGlobal } from 'Shared/utils/global-events';
import { goToLastPage } from 'Shared/utils/last-page';
import { authenticateWithCode } from '../services/authService';
import { withLoadingIndicator } from '../utils/loadingUtils';
import type { AuthResponse } from '../types';
import type { TranslationFunction } from '@ifixit/i18n/types';

interface UseLoginCodeProps {
   email: string;
   onNavigateBack: () => void;
}

/**
 * Validates that the code is exactly 6 digits
 */
const validateLoginCode = (code: string, t: TranslationFunction): string => {
   if (code.length !== 6) {
      return t('pleaseEnterSixDigitCode');
   }
   return '';
};

/**
 * Maps authentication error responses to user-friendly messages
 */
const getErrorMessage = (response: AuthResponse, t: TranslationFunction): string => {
   const messages = (window as any).App?.loginMessages;

   switch (response.reason) {
      case 'email': {
         return messages?.invalid_email || t('invalidEmailAddress');
      }
      case 'password': {
         return messages?.invalid_code || t('invalidVerificationCode');
      }
      case 'tooManyCodeAttempts': {
         return messages?.tooManyCodeAttempts || t('tooManyCodeAttempts');
      }
      default: {
         return response.message || response.reason || t('loginFailedPleaseTryAgain');
      }
   }
};

/**
 * Handles successful login by firing events and redirecting
 */
const handleLoginSuccess = (response: AuthResponse) => {
   fireGlobal('login.success', response);
   goToLastPage();
};

/**
 * Handles failed login by firing events and returning error message
 */
const handleLoginFailure = (response: AuthResponse, t: TranslationFunction): string => {
   fireGlobal('login.failure', response);
   return getErrorMessage(response, t);
};

export const useLoginCode = ({ email, onNavigateBack }: UseLoginCodeProps) => {
   const t = useTranslations('Login');
   const [code, setCode] = useState('');
   const [error, setError] = useState<string>('');

   const loginMutation = useMutation({
      mutationFn: ({ email, code }: { email: string; code: string }) =>
         authenticateWithCode(email, code),
      onSuccess: handleLoginSuccess,
      onError: (response: AuthResponse) => {
         setError(handleLoginFailure(response, t));
         setCode(''); // Clear code on failure
      },
   });

   const clearError = useCallback(() => {
      setError('');
   }, []);

   const submitCode = useCallback(async () => {
      const validationError = validateLoginCode(code, t);
      if (validationError) {
         setError(validationError);
         return;
      }

      setError('');

      try {
         await withLoadingIndicator(loginMutation.mutateAsync({ email, code }), {
            pendingMessage: t('verifyingCode'),
            successMessage: t('loginSuccessful'),
            failureMessage: t('codeVerificationFailed'),
         });
      } catch (error) {
         console.error('Error during login:', error);
         // Error is already handled in onError callback
      }
   }, [code, email, loginMutation, t]);

   return {
      code,
      setCode,
      isSubmitting: loginMutation.isPending,
      error,
      clearError,
      submitCode,
      navigateBack: onNavigateBack,
   };
};
